import MenuItem from "@mui/material/MenuItem"
import FormControl from "@mui/material/FormControl"
import Select from "@mui/material/Select"
import InputLabel from "@mui/material/InputLabel"
import FormControlLabel from "@mui/material/FormControlLabel"
import Checkbox from "@mui/material/Checkbox"
import {
  Accordion,
  AccordionDetails,
  AccordionSummary,
  Button,
  FormGroup,
} from "@mui/material"
import { get_number_geojson_properties } from "../../utils/layer_actions"
import Slider from "@mui/material/Slider"
import Typography from "@mui/material/Typography"
import ExpandMoreIcon from "@mui/icons-material/ExpandMore"
import Search from "./Search"
import facility_types from "../../configs/facility_types.json"
import theme_colours from "../../configs/theme_colors.json"

const { cups } = theme_colours

const GenericToggle = ({
  visualisation_settings,
  set_visualisation_settings,
  property,
  label,
  symbol,
  symbol_color,
}) => {
  return (
    <FormControlLabel
      control={
        <Checkbox
          checked={visualisation_settings[property]}
          onChange={(event) => {
            set_visualisation_settings((current_settings) => {
              let new_settings = { ...current_settings }
              new_settings[property] = event.target.checked
              return new_settings
            })
          }}
        />
      }
      label={
        <>
          {symbol && (
            <span style={{ color: symbol_color, marginRight: 4 }}>
              {symbol}
            </span>
          )}
          {label}
        </>
      }
    />
  )
}

const FacilityToggle = ({
  visualisation_settings,
  set_visualisation_settings,
  property,
  label,
  symbol,
  symbol_color,
}) => {
  return (
    <FormControlLabel
      control={
        <Checkbox
          checked={visualisation_settings.visible_facilities.includes(property)}
          onChange={(event) => {
            set_visualisation_settings((current_settings) => {
              // Ensure visible_facilities is always an array
              const visible = Array.isArray(current_settings.visible_facilities)
                ? current_settings.visible_facilities
                : []

              let new_visible
              if (event.target.checked) {
                // Add property if not already in the array
                new_visible = visible.includes(property)
                  ? visible
                  : [...visible, property]
              } else {
                // Remove property if present
                new_visible = visible.filter((el) => el !== property)
              }

              return {
                ...current_settings,
                visible_facilities: new_visible,
              }
            })
          }}
        />
      }
      label={
        <>
          {symbol && (
            <span style={{ color: symbol_color, marginRight: 4 }}>
              {symbol}
            </span>
          )}
          {label}
        </>
      }
    />
  )
}

const GenericSlider = ({
  visualisation_settings,
  set_visualisation_settings,
  property,
  label,
  min,
  max,
  step,
}) => {
  return (
    <FormControl fullWidth margin="dense" variant="standard">
      <InputLabel shrink={true}>{label}</InputLabel>
      <Slider
        value={visualisation_settings[property]}
        style={{ marginTop: 10 }}
        min={min}
        max={max}
        step={step}
        onChange={(event) => {
          set_visualisation_settings((current_settings) => {
            let new_settings = { ...current_settings }
            new_settings[property] = event.target.value
            return new_settings
          })
        }}
      />
    </FormControl>
  )
}

const Visualisation = ({
  visualisation_settings,
  set_visualisation_settings,
  map_ref,
  map_data,
}) => {
  const { fac_geo_json, au_geo_json, cup_geo_json } = map_data
  const au_render_options = get_number_geojson_properties(au_geo_json)

  return (
    <div style={{ maxHeight: "calc(100vh - 120px)", overflow: "auto" }}>
      <Accordion>
        <AccordionSummary expandIcon={<ExpandMoreIcon />}>
          <Typography variant="button">Selectors</Typography>
        </AccordionSummary>
        <AccordionDetails>
          <FormControl fullWidth variant="standard">
            <InputLabel shrink>Render Property</InputLabel>
            <Select
              value={visualisation_settings.au_render_prop}
              onChange={(event) => {
                const property = event.target.value
                set_visualisation_settings((current_settings) => {
                  let new_settings = { ...current_settings }
                  new_settings.au_render_prop = property
                  return new_settings
                })
              }}
              displayEmpty
              renderValue={(selected) => {
                if (selected === "") {
                  return "none"
                }
                return selected
              }}
            >
              <MenuItem value={""}>none</MenuItem>
              {au_render_options.map((option, index) => {
                return (
                  <MenuItem key={index} value={option}>
                    {option}
                  </MenuItem>
                )
              })}
            </Select>
          </FormControl>
        </AccordionDetails>
      </Accordion>
      <Accordion>
        <AccordionSummary expandIcon={<ExpandMoreIcon />}>
          <Typography variant="button">Sliders</Typography>
        </AccordionSummary>
        <AccordionDetails>
          <GenericSlider
            visualisation_settings={visualisation_settings}
            set_visualisation_settings={set_visualisation_settings}
            property={"facility_radius"}
            label={"Facility Dot Radius"}
            min={1}
            max={10}
            step={1}
          />
          <GenericSlider
            visualisation_settings={visualisation_settings}
            set_visualisation_settings={set_visualisation_settings}
            property={"analysis_units_opacity"}
            label={"Analysis Units Opacity"}
            min={0}
            max={1}
            step={0.1}
          />
        </AccordionDetails>
      </Accordion>
      <Accordion>
        <AccordionSummary expandIcon={<ExpandMoreIcon />}>
          <Typography variant="button">Points</Typography>
        </AccordionSummary>
        <AccordionDetails>
          <FormGroup>
            <GenericToggle
              visualisation_settings={visualisation_settings}
              set_visualisation_settings={set_visualisation_settings}
              property={"show_fac_labels"}
              label={"Names"}
            />

            <GenericToggle
              visualisation_settings={visualisation_settings}
              set_visualisation_settings={set_visualisation_settings}
              property={"show_cups"}
              label={"CUPs"}
              symbol={"■"}
              symbol_color={cups}
            />

            {facility_types.map(({ colour, name, symbol, type }, i) => (
              <FacilityToggle
                key={i}
                visualisation_settings={visualisation_settings}
                set_visualisation_settings={set_visualisation_settings}
                property={type}
                label={name}
                symbol={symbol}
                symbol_color={colour}
              />
            ))}
          </FormGroup>
        </AccordionDetails>
      </Accordion>
      <Accordion>
        <AccordionSummary expandIcon={<ExpandMoreIcon />}>
          <Typography variant="button">Boundaries</Typography>
        </AccordionSummary>
        <AccordionDetails>
          <FormGroup>
            <GenericToggle
              visualisation_settings={visualisation_settings}
              set_visualisation_settings={set_visualisation_settings}
              property={"show_au_layer"}
              label={"Analysis Units"}
            />

            <GenericToggle
              visualisation_settings={visualisation_settings}
              set_visualisation_settings={set_visualisation_settings}
              property={"show_wards"}
              label={"Wards"}
            />

            <GenericToggle
              visualisation_settings={visualisation_settings}
              set_visualisation_settings={set_visualisation_settings}
              property={"show_sub_districts"}
              label={"Sub Districts"}
            />

            <GenericToggle
              visualisation_settings={visualisation_settings}
              set_visualisation_settings={set_visualisation_settings}
              property={"show_districts"}
              label={"Districts"}
            />

            <GenericToggle
              visualisation_settings={visualisation_settings}
              set_visualisation_settings={set_visualisation_settings}
              property={"show_provinces"}
              label={"Provinces"}
            />
          </FormGroup>
        </AccordionDetails>
      </Accordion>

      <Accordion>
        <AccordionSummary expandIcon={<ExpandMoreIcon />}>
          <Typography variant="button">Miscellaneous</Typography>
        </AccordionSummary>
        <AccordionDetails>
          <Button
            fullWidth
            onClick={() => {
              map_ref.setPitch(0)
              map_ref.rotateTo(0)
            }}
          >
            Reset Map
          </Button>
          <Search
            map_ref={map_ref}
            fac_geo_json={fac_geo_json}
            cup_geo_json={cup_geo_json}
          />
        </AccordionDetails>
      </Accordion>
    </div>
  )
}

export default Visualisation
